import React, { useState, useEffect } from 'react';
import { scheduleAPI, ScheduleItem } from '../../api/api';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import './AdminPanel.css';

const daysOfWeek = ['Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота', 'Воскресенье'];

export const ScheduleAdmin: React.FC = () => {
    const [schedule, setSchedule] = useState<ScheduleItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [showConfirm, setShowConfirm] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<number | null>(null);

    const [formData, setFormData] = useState({
        day_of_week: 'Понедельник',
        subject: '',
        time_start: '',
        time_end: '',
        room: '',
        teacher_name: '',
    });

    useEffect(() => {
        loadSchedule();
    }, []);

    const loadSchedule = async () => {
        try {
            setLoading(true);
            const data = await scheduleAPI.getAll();
            setSchedule(data);
            setError(null);
        } catch (err) {
            setError('Не удалось загрузить расписание');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const resetForm = () => {
        setFormData({
            day_of_week: 'Понедельник',
            subject: '',
            time_start: '',
            time_end: '',
            room: '',
            teacher_name: '',
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.day_of_week || !formData.subject) {
            alert('Заполните обязательные поля!');
            return;
        }

        const submitData = new FormData();
        submitData.append('day_of_week', formData.day_of_week);
        submitData.append('subject', formData.subject);
        submitData.append('time_start', formData.time_start);
        submitData.append('time_end', formData.time_end);
        submitData.append('room', formData.room);
        submitData.append('teacher_name', formData.teacher_name);

        try {
            if (editingId) {
                submitData.append('id', editingId.toString());
                await scheduleAPI.update(editingId, submitData);
            } else {
                await scheduleAPI.add(submitData);
            }

            resetForm();
            setShowForm(false);
            setEditingId(null);
            loadSchedule();
        } catch (err) {
            alert('Ошибка при сохранении');
            console.error(err);
        }
    };

    const handleEdit = (item: ScheduleItem) => {
        setFormData({
            day_of_week: item.day_of_week,
            subject: item.subject,
            time_start: item.time_start || '',
            time_end: item.time_end || '',
            room: item.room || '',
            teacher_name: item.teacher_name || '',
        });
        setEditingId(item.id);
        setShowForm(true);
    };

    const handleDeleteClick = (id: number) => {
        setItemToDelete(id);
        setShowConfirm(true);
    };

    const handleConfirmDelete = async () => {
        if (itemToDelete) {
            try {
                await scheduleAPI.delete(itemToDelete);
                loadSchedule();
            } catch (err) {
                alert('Ошибка при удалении');
                console.error(err);
            } finally {
                setShowConfirm(false);
                setItemToDelete(null);
            }
        }
    };

    const handleCancelDelete = () => {
        setShowConfirm(false);
        setItemToDelete(null);
    };

    const cancelEdit = () => {
        setShowForm(false);
        setEditingId(null);
        resetForm();
    };

    if (loading) return <div>Загрузка...</div>;
    if (error) return <div className="error">{error}</div>;

    // Группируем по дням недели
    const scheduleByDay = daysOfWeek.reduce((acc, day) => {
        acc[day] = schedule.filter(item => item.day_of_week === day);
        return acc;
    }, {} as Record<string, ScheduleItem[]>);

    return (
        <div className="admin-panel">
            <div className="admin-panel-header">
                <h2>Управление расписанием</h2>
                <button className="btn-add" onClick={() => { setShowForm(!showForm); setEditingId(null); resetForm(); }}>
                    {showForm ? 'Отмена' : '+ Добавить занятие'}
                </button>
            </div>

            {showForm && (
                <form className="review-form" onSubmit={handleSubmit}>
                    <h3>{editingId ? 'Редактировать занятие' : 'Новое занятие'}</h3>

                    <div className="form-row">
                        <div className="form-group">
                            <label>День недели: *</label>
                            <select name="day_of_week" value={formData.day_of_week} onChange={handleInputChange} required>
                                {daysOfWeek.map(day => (
                                    <option key={day} value={day}>{day}</option>
                                ))}
                            </select>
                        </div>

                        <div className="form-group">
                            <label>Предмет: *</label>
                            <input type="text" name="subject" value={formData.subject} onChange={handleInputChange} required />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>Время начала:</label>
                            <input type="time" name="time_start" value={formData.time_start} onChange={handleInputChange} />
                        </div>

                        <div className="form-group">
                            <label>Время окончания:</label>
                            <input type="time" name="time_end" value={formData.time_end} onChange={handleInputChange} />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>Кабинет:</label>
                            <input type="text" name="room" value={formData.room} onChange={handleInputChange} placeholder="Например: 101" />
                        </div>

                        <div className="form-group">
                            <label>Преподаватель:</label>
                            <input type="text" name="teacher_name" value={formData.teacher_name} onChange={handleInputChange} />
                        </div>
                    </div>

                    <div className="form-actions">
                        <button type="submit" className="btn-save">{editingId ? 'Сохранить' : 'Добавить'}</button>
                        <button type="button" className="btn-cancel" onClick={cancelEdit}>Отмена</button>
                    </div>
                </form>
            )}

            {/* Таблица расписания */}
            <div className="schedule-table-container">
                <table className="schedule-table">
                    <thead>
                    <tr>
                        <th>День недели</th>
                        <th>Предмет</th>
                        <th>Время</th>
                        <th>Кабинет</th>
                        <th>Преподаватель</th>
                        <th>Действия</th>
                    </tr>
                    </thead>
                    <tbody>
                    {daysOfWeek.map(day => (
                        scheduleByDay[day].length > 0 ? (
                            scheduleByDay[day].map((item, index) => (
                                <tr key={item.id}>
                                    {index === 0 && (
                                        <td rowSpan={scheduleByDay[day].length} className="day-cell">
                                            {day}
                                        </td>
                                    )}
                                    <td>{item.subject}</td>
                                    <td>
                                        {item.time_start && item.time_end
                                            ? `${item.time_start.substring(0, 5)} - ${item.time_end.substring(0, 5)}`
                                            : '-'
                                        }
                                    </td>
                                    <td>{item.room || '-'}</td>
                                    <td>{item.teacher_name || '-'}</td>
                                    <td>
                                        <button className="btn-edit-small" onClick={() => handleEdit(item)}>✏️</button>
                                        <button className="btn-delete-small" onClick={() => handleDeleteClick(item.id)}>🗑️</button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr key={day}>
                                <td className="day-cell">{day}</td>
                                <td colSpan={4} style={{color: '#999'}}>Нет занятий</td>
                                <td>-</td>
                            </tr>
                        )
                    ))}
                    </tbody>
                </table>
            </div>

            {showConfirm && itemToDelete && (
                <ConfirmDialog
                    message="Удалить это занятие из расписания?"
                    onConfirm={handleConfirmDelete}
                    onCancel={handleCancelDelete}
                />
            )}
        </div>
    );
};